/**
 * ArcadeModule — the Arcade tab (A1).
 *
 * The launch-shell tab is visible-but-LOCKED until `unlockArcadeTab` flips
 * `arcadeTabState`; once live it renders the game browser (visibility/safety
 * filtered the same way the server enforces) and the session lobby. The client
 * RENDERS replicated rows + CALLS reducers — the competitive jurisdiction, results
 * authority (A3), and visibility are all server-side. Sessions never self-finish
 * here (finished/aborted are platform-authoritative).
 */

import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { iconMarkup } from '../lib/icons.js';

export class ArcadeModule implements IModule {
  readonly name = 'Arcade';
  private bound = false;
  private els: Record<string, HTMLElement> = {};
  private seq = new Map<string, number>(); // per-session intent sequence (A3 anti-replay)

  init(_app: App): void {
    for (const id of ['arc-gate', 'arc-browser', 'arc-queue', 'arc-sessions', 'arc-leaderboard', 'arc-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = (): void => this.render();
    for (const tbl of [conn.db.arcadeTabState, conn.db.arcadeGame, conn.db.arcadeGameDef, conn.db.gameSession, conn.db.rating, conn.db.matchTicket, conn.db.matchParticipant, conn.db.matchScore, conn.db.cheatFlag, conn.db.matchVerdict, conn.db.leaderboard, conn.db.arcadeSeason]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
  }

  /** Truthy read — the single-row tab table round-trips `unlocked` as 0/1. */
  private unlocked(conn: DbConnection): boolean {
    const row = [...conn.db.arcadeTabState.iter()][0];
    return row ? Boolean(row.unlocked) : false;
  }

  /** The server-authoritative visibility/safety filter (Commons + public/unlisted, not quarantined). */
  private browsable(conn: DbConnection, pinnedTo: bigint): boolean {
    if (pinnedTo === 0n) return true;
    const world = [...conn.db.world.iter()].find((w) => w.worldId === pinnedTo);
    if (!world) return false;
    const gov = [...conn.db.worldGovernance.iter()].find((g) => g.worldId === pinnedTo);
    if (gov?.quarantined) return false;
    return world.visibility === 'public' || world.visibility === 'unlisted';
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.renderGate(conn);
    this.renderBrowser(conn);
    this.renderQueue(conn);
    this.renderSessions(conn);
    this.renderLeaderboard(conn);
  }

  /** A4: per-game leaderboards — top-N, your rank + percentile (read from the authoritative board). */
  private renderLeaderboard(conn: DbConnection): void {
    const el = this.els['arc-leaderboard'];
    if (!el) return;
    if (!this.unlocked(conn)) {
      el.innerHTML = '';
      return;
    }
    const me = conn.identity;
    const games = new Set<string>();
    for (const r of conn.db.leaderboard.iter()) games.add(r.gameId.toString());
    if (games.size === 0) {
      el.innerHTML = '<div class="empty">No leaderboards yet — play a verified match.</div>';
      return;
    }
    const parts: string[] = [];
    for (const gid of [...games].slice(0, 4)) {
      const gameId = BigInt(gid);
      const rows = [...conn.db.leaderboard.iter()].filter((r) => r.gameId === gameId).sort((a, b) => (a.bestScore > b.bestScore ? -1 : a.bestScore < b.bestScore ? 1 : 0));
      const total = rows.length;
      const season = [...conn.db.arcadeSeason.iter()].find((s) => s.gameId === gameId && s.status === 'active');
      parts.push(`<div class="meta">${iconMarkup('trophy')}game #${gid}${season ? ` · season ${season.seasonNum}` : ''} · ${total} ranked</div>`);
      for (const r of rows.slice(0, 5)) {
        const mine = me && r.player.isEqual(me) ? ' ← you' : '';
        parts.push(`<div class="meta">&nbsp;&nbsp;#${r.rank} ${r.player.toHexString().slice(0, 8)} — ${r.bestScore}${mine}</div>`);
      }
      if (me) {
        const myRow = rows.find((r) => r.player.isEqual(me));
        if (myRow && myRow.rank > 5) {
          const p = total > 0 ? myRow.rank / total : 1;
          const tier = p <= 0.01 ? 'top 1%' : p <= 0.1 ? 'top 10%' : p <= 0.5 ? 'top 50%' : 'ranked';
          parts.push(`<div class="meta">&nbsp;&nbsp;your rank #${myRow.rank}/${total} (${tier}) — ${myRow.bestScore}</div>`);
        }
      }
    }
    el.innerHTML = parts.join('');
  }

  private renderGate(conn: DbConnection): void {
    const el = this.els['arc-gate'];
    if (!el) return;
    el.innerHTML = this.unlocked(conn)
      ? `<div class="meta">${iconMarkup('gamepad')}The Arcade is LIVE — browse + launch games below.</div>`
      : `<div class="empty">${iconMarkup('lock')}The Arcade is unlocking soon (visible-but-locked launch tab).</div>`;
  }

  private renderBrowser(conn: DbConnection): void {
    const el = this.els['arc-browser'];
    if (!el) return;
    if (!this.unlocked(conn)) {
      el.innerHTML = '';
      return;
    }
    const games = [...conn.db.arcadeGame.iter()].filter((g) => g.pinned && this.browsable(conn, g.pinnedTo));
    if (games.length === 0) {
      el.innerHTML = '<div class="empty">No pinned games yet.</div>';
      return;
    }
    const lines: string[] = [];
    for (const g of games.slice(0, 20)) {
      const def = conn.db.arcadeGameDef.gameId.find(g.gameId);
      const where = g.pinnedTo === 0n ? 'Commons' : `world #${g.pinnedTo}`;
      lines.push(
        `<div class="meta">${iconMarkup('gamepad')}game #${g.gameId} · ${this.esc(g.mode)} · ${where} · <span class="price">${def ? this.esc(def.jurisdiction) : '?'}</span> <button class="action secondary" data-open="${g.gameId.toString()}">Open lobby</button> <button class="action secondary" data-queue="${g.gameId.toString()}">Queue</button></div>`,
      );
    }
    el.innerHTML = lines.join('');
    for (const btn of Array.from(el.querySelectorAll('[data-open]'))) {
      const gid = BigInt((btn as HTMLElement).dataset.open!);
      const g = conn.db.arcadeGame.gameId.find(gid);
      btn.addEventListener('click', () => {
        if (g) this.openSession(gid, g.pinnedTo);
      });
    }
    for (const btn of Array.from(el.querySelectorAll('[data-queue]'))) {
      btn.addEventListener('click', () => this.queue(BigInt((btn as HTMLElement).dataset.queue!)));
    }
  }

  /** A2: the player's queue status, recent match-found, and per-game OpenSkill ratings. */
  private renderQueue(conn: DbConnection): void {
    const el = this.els['arc-queue'];
    if (!el) return;
    const me = conn.identity;
    if (!this.unlocked(conn) || !me) {
      el.innerHTML = '';
      return;
    }
    const parts: string[] = [];
    for (const tk of conn.db.matchTicket.iter()) {
      if (tk.player.isEqual(me) && !tk.matched) parts.push(`<div class="meta">⏳ Queued — game #${tk.gameId} (region ${this.esc(tk.region)}, skill ${tk.skill.toFixed(0)})</div>`);
    }
    const mine = [...conn.db.matchParticipant.iter()].filter((p) => p.player.isEqual(me)).sort((a, b) => (a.sessionId < b.sessionId ? 1 : -1));
    for (const p of mine.slice(0, 3)) parts.push(`<div class="meta">${iconMarkup('check')}Match found — session #${p.sessionId} (game #${p.gameId}, team ${p.team})</div>`);
    for (const r of [...conn.db.rating.iter()].filter((x) => x.player.isEqual(me)).slice(0, 5)) {
      parts.push(`<div class="meta">${iconMarkup('chart')}game #${r.gameId}: skill ${(r.mu - 3 * r.sigma).toFixed(1)} (μ${r.mu.toFixed(1)} σ${r.sigma.toFixed(1)}, ${r.matches} matches)</div>`);
    }
    el.innerHTML = parts.length ? parts.join('') : '<div class="empty">Not queued — pick a game above to queue for matchmaking.</div>';
  }

  private renderSessions(conn: DbConnection): void {
    const el = this.els['arc-sessions'];
    if (!el) return;
    if (!this.unlocked(conn)) {
      el.innerHTML = '';
      return;
    }
    const me = conn.identity;
    const sessions = [...conn.db.gameSession.iter()].sort((a, b) => (a.sessionId < b.sessionId ? 1 : -1)).slice(0, 8);
    if (sessions.length === 0) {
      el.innerHTML = '';
      return;
    }
    const lines: string[] = ['<h3>Sessions</h3>'];
    for (const s of sessions) {
      const isPart = me ? [...conn.db.matchParticipant.iter()].some((p) => p.sessionId === s.sessionId && p.player.isEqual(me)) : false;
      const v = conn.db.matchVerdict.sessionId.find(s.sessionId);
      const vbadge = v
        ? v.excluded
          ? '<span class="price" style="color:var(--ax-bad)">✗ excluded</span>'
          : v.verified
            ? '<span class="price">✓ verified</span>'
            : v.scored
              ? '<span class="price">… scored</span>'
              : ''
        : '';
      const start = s.state === 'lobby' && isPart ? ` <button class="action secondary" data-start="${s.sessionId.toString()}">Start</button>` : '';
      const intent = s.state === 'active' && isPart ? ` <button class="action secondary" data-intent="${s.sessionId.toString()}">Score +10</button>` : '';
      lines.push(`<div class="meta">session #${s.sessionId} · game #${s.gameId} · <b>${this.esc(s.state)}</b> ${vbadge}${start}${intent}</div>`);
      if (me) {
        const ms = [...conn.db.matchScore.iter()].find((x) => x.sessionId === s.sessionId && x.player.isEqual(me));
        if (ms) lines.push(`<div class="meta">&nbsp;&nbsp;your score ${ms.score} (placement ${ms.placement + 1})</div>`);
      }
      for (const f of conn.db.cheatFlag.iter()) {
        if (f.sessionId === s.sessionId) lines.push(`<div class="meta" style="color:var(--ax-bad)">&nbsp;&nbsp;⚑ ${this.esc(f.kind)} → ${this.esc(f.action)} (${(f.confidence * 100).toFixed(0)}%)</div>`);
      }
    }
    el.innerHTML = lines.join('');
    for (const btn of Array.from(el.querySelectorAll('[data-start]'))) {
      btn.addEventListener('click', () => this.setActive(BigInt((btn as HTMLElement).dataset.start!)));
    }
    for (const btn of Array.from(el.querySelectorAll('[data-intent]'))) {
      btn.addEventListener('click', () => this.submitScoreIntent(BigInt((btn as HTMLElement).dataset.intent!)));
    }
  }

  private openSession(gameId: bigint, worldId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.openSession({ gameId, worldId }).then(() => this.note('Lobby opened.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private setActive(sessionId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.setSessionState({ sessionId, state: 'active' }).then(() => this.note('Match started.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  private queue(gameId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.enqueueMatch({ gameId, region: 'global' }).then(() => this.note('Queued for matchmaking.', 'ok')).catch((e: Error) => this.note(e.message, 'err'));
  }

  /** A3: send a scoring INTENT (not a score). The server validates + tallies authoritatively. */
  private submitScoreIntent(sessionId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const key = sessionId.toString();
    const nextSeq = (this.seq.get(key) ?? 0) + 1;
    this.seq.set(key, nextSeq);
    const nonce = `${key}-${nextSeq}-${Math.floor(Date.now() * (1 + Math.random()))}`;
    void conn.reducers
      .submitIntent({ sessionId, seq: nextSeq, nonce, tsMicros: BigInt(Date.now()) * 1000n, intent: JSON.stringify({ action: 'score', value: 10 }) })
      .then(() => this.note('Intent submitted — the server validates + tallies the score.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private note(text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els['arc-note'];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }

  dispose(): void {}
}
